import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  Tooltip,
} from "@mui/material";
import GridViewIcon from "@mui/icons-material/GridView";
import SwitchAccountIcon from "@mui/icons-material/SwitchAccount";
import AddToHomeScreenIcon from "@mui/icons-material/AddToHomeScreen";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import StorefrontIcon from "@mui/icons-material/Storefront";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import ProductionQuantityLimitsIcon from "@mui/icons-material/ProductionQuantityLimits";
import BorderAllIcon from "@mui/icons-material/BorderAll";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import SettingsIcon from "@mui/icons-material/Settings";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EmojiTransportationIcon from "@mui/icons-material/EmojiTransportation";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import SendIcon from "@mui/icons-material/Send";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Logo from "../../../assets/Logo.png";

const SIDEBAR_BG = "rgb(17, 24, 39)";
const ACTIVE_BG = "rgba(255,255,255,0.1)";
const TEXT_COLOR = "rgba(224,224,224,0.85)";
const ACCENT_COLOR = "rgb(110, 122, 242)";

const NavItem = ({ icon, label, to, collapsed, active, onClick }) => (
  <Tooltip title={collapsed ? label : ""} placement="right">
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: collapsed ? 1.5 : 2,
        py: 1,
        mx: 1,
        borderRadius: "10px",
        cursor: "pointer",
        backgroundColor: active ? ACTIVE_BG : "transparent",
        color: active ? "#fff" : TEXT_COLOR,
        fontWeight: active ? 600 : 400,
        transition: "all 0.15s",
        justifyContent: collapsed ? "center" : "flex-start",
        "&:hover": { backgroundColor: ACTIVE_BG, color: "#fff" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", minWidth: 22, color: active ? ACCENT_COLOR : "inherit" }}>
        {icon}
      </Box>
      {!collapsed && (
        <Typography sx={{ fontSize: "13.5px", fontWeight: "inherit", color: "inherit", whiteSpace: "nowrap" }}>
          {label}
        </Typography>
      )}
    </Box>
  </Tooltip>
);

const NavGroup = ({ icon, label, collapsed, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  if (collapsed) {
    return (
      <Box>
        <Tooltip title={label} placement="right">
          <Box
            onClick={() => setOpen(!open)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 1.5,
              py: 1,
              mx: 1,
              borderRadius: "10px",
              cursor: "pointer",
              color: TEXT_COLOR,
              "&:hover": { backgroundColor: ACTIVE_BG, color: "#fff" },
            }}
          >
            {icon}
          </Box>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          mx: 1,
          borderRadius: "10px",
          cursor: "pointer",
          color: TEXT_COLOR,
          "&:hover": { backgroundColor: ACTIVE_BG, color: "#fff" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", minWidth: 22 }}>{icon}</Box>
          <Typography sx={{ fontSize: "13.5px", fontWeight: 500, whiteSpace: "nowrap" }}>{label}</Typography>
        </Box>
        {open ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
      </Box>
      <Collapse in={open}>
        <Box sx={{ pl: 1 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

const Sidebar = ({ collapsed, setCollapsed }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const go = (path) => navigate(path);
  const is = (path) => pathname === path;

  return (
    <Box
      sx={{
        width: collapsed ? 64 : 240,
        minWidth: collapsed ? 64 : 240,
        height: "100vh",
        backgroundColor: SIDEBAR_BG,
        display: "flex",
        flexDirection: "column",
        transition: "width 0.2s ease, min-width 0.2s ease",
        overflowX: "hidden",
        overflowY: "auto",
        position: "sticky",
        top: 0,
        zIndex: 100,
        "&::-webkit-scrollbar": { width: "4px" },
        "&::-webkit-scrollbar-thumb": { background: "rgba(255,255,255,0.1)", borderRadius: "4px" },
      }}
    >
      {/* Logo + Toggle */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          px: collapsed ? 1 : 2,
          py: 2,
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          minHeight: 64,
        }}
      >
        {!collapsed && (
          <img src={Logo} alt="logo" style={{ width: "90px", objectFit: "contain" }} />
        )}
        <IconButton
          onClick={() => setCollapsed(!collapsed)}
          size="small"
          sx={{ color: TEXT_COLOR, "&:hover": { color: "#fff", backgroundColor: ACTIVE_BG } }}
        >
          {collapsed ? <MenuIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Nav */}
      <Box sx={{ flex: 1, py: 1.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
        {!collapsed && (
          <Typography
            sx={{ fontSize: "11px", fontWeight: 700, color: ACCENT_COLOR, px: 3, pb: 0.5, textTransform: "uppercase", letterSpacing: "0.08em" }}
          >
            Main
          </Typography>
        )}

        <NavItem icon={<GridViewIcon sx={{ fontSize: 20 }} />} label="Dashboard" to="/" collapsed={collapsed} active={is("/")} onClick={() => go("/")} />

        {!collapsed && (
          <Typography
            sx={{ fontSize: "11px", fontWeight: 700, color: ACCENT_COLOR, px: 3, pt: 1.5, pb: 0.5, textTransform: "uppercase", letterSpacing: "0.08em" }}
          >
            Users
          </Typography>
        )}

        <NavGroup icon={<SwitchAccountIcon sx={{ fontSize: 20 }} />} label="Manage Users" collapsed={collapsed}>
          <NavItem icon={<AddToHomeScreenIcon sx={{ fontSize: 18 }} />} label="Incomplete Request" to="/incomplete" collapsed={collapsed} active={is("/incomplete")} onClick={() => go("/incomplete")} />
          <NavItem icon={<AddToHomeScreenIcon sx={{ fontSize: 18 }} />} label="User Request" to="/userRequest" collapsed={collapsed} active={is("/userRequest")} onClick={() => go("/userRequest")} />
          <NavItem icon={<ShoppingCartIcon sx={{ fontSize: 18 }} />} label="Buyers" to="/buyer" collapsed={collapsed} active={is("/buyer")} onClick={() => go("/buyer")} />
          <NavItem icon={<StorefrontIcon sx={{ fontSize: 18 }} />} label="Sellers" to="/seller" collapsed={collapsed} active={is("/seller")} onClick={() => go("/seller")} />
        </NavGroup>

        {!collapsed && (
          <Typography
            sx={{ fontSize: "11px", fontWeight: 700, color: ACCENT_COLOR, px: 3, pt: 1.5, pb: 0.5, textTransform: "uppercase", letterSpacing: "0.08em" }}
          >
            Catalogue
          </Typography>
        )}

        <NavItem icon={<MenuOpenIcon sx={{ fontSize: 20 }} />} label="Category" to="/category" collapsed={collapsed} active={is("/category")} onClick={() => go("/category")} />

        <NavGroup icon={<ProductionQuantityLimitsIcon sx={{ fontSize: 20 }} />} label="Product" collapsed={collapsed}>
          <NavItem icon={<ProductionQuantityLimitsIcon sx={{ fontSize: 18 }} />} label="Products" to="/product" collapsed={collapsed} active={is("/product")} onClick={() => go("/product")} />
        </NavGroup>

        {!collapsed && (
          <Typography
            sx={{ fontSize: "11px", fontWeight: 700, color: ACCENT_COLOR, px: 3, pt: 1.5, pb: 0.5, textTransform: "uppercase", letterSpacing: "0.08em" }}
          >
            Orders
          </Typography>
        )}

        <NavGroup icon={<BorderAllIcon sx={{ fontSize: 20 }} />} label="Orders" collapsed={collapsed} defaultOpen>
          <NavItem icon={<LibraryBooksIcon sx={{ fontSize: 18 }} />} label="Buyer Orders" to="/buyerOrder" collapsed={collapsed} active={is("/buyerOrder")} onClick={() => go("/buyerOrder")} />
          <NavItem icon={<StorefrontIcon sx={{ fontSize: 18 }} />} label="Seller Orders" to="/sellerOrder" collapsed={collapsed} active={is("/sellerOrder")} onClick={() => go("/sellerOrder")} />
        </NavGroup>

        {!collapsed && (
          <Typography
            sx={{ fontSize: "11px", fontWeight: 700, color: ACCENT_COLOR, px: 3, pt: 1.5, pb: 0.5, textTransform: "uppercase", letterSpacing: "0.08em" }}
          >
            Settings
          </Typography>
        )}

        <NavGroup icon={<SettingsIcon sx={{ fontSize: 20 }} />} label="Settings" collapsed={collapsed}>
          <NavItem icon={<LocationOnIcon sx={{ fontSize: 18 }} />} label="Locations" to="/state" collapsed={collapsed} active={is("/state")} onClick={() => go("/state")} />
          <NavItem icon={<EmojiTransportationIcon sx={{ fontSize: 18 }} />} label="Transport" to="/transport" collapsed={collapsed} active={is("/transport")} onClick={() => go("/transport")} />
          <NavItem icon={<TextSnippetIcon sx={{ fontSize: 18 }} />} label="Terms & Conditions" to="/terms" collapsed={collapsed} active={is("/terms")} onClick={() => go("/terms")} />
          <NavItem icon={<SendIcon sx={{ fontSize: 18 }} />} label="Whatsapp Message" to="/whatshappmessage" collapsed={collapsed} active={is("/whatshappmessage")} onClick={() => go("/whatshappmessage")} />
        </NavGroup>
      </Box>
    </Box>
  );
};

export default Sidebar;
